import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Crown, Trophy, Star, MapPin, DollarSign, Shield, Sword, Users, Flame, Hash, Target, ChevronLeft } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

// =============================================
// HALL OF FAME
// =============================================
const HallOfFame = ({ onBack }) => {
  const [hall, setHall] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/hall/fame`).then(r => r.json()).then(d => setHall(d.hall || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const CATEGORY_ICONS = {
    first_all_eras: <Star size={16} />,
    first_all_brooklyn: <MapPin size={16} />,
    table_undefeated: <Users size={16} />,
    most_consecutive_logins: <Flame size={16} />,
    all_time_earner: <DollarSign size={16} />,
    territory_king: <Crown size={16} />,
    waterfront_conqueror: <Crown size={16} />,
    war_champion: <Shield size={16} />,
    challenge_master: <Sword size={16} />,
    numbers_king: <Hash size={16} />,
  };

  if (loading) return <p className="font-['Outfit'] text-[#E8E0D0]/20 text-xs text-center py-8">Loading...</p>;

  return (
    <div className="space-y-4" data-testid="hall-of-fame">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest hover:text-[#C9A84C]">BACK</button>
      </div>
      <div className="text-center">
        <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#FFD700]" data-testid="hall-title">HALL OF FAME</h3>
        <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest">PERMANENT RECORDS — THE WATER REMEMBERS</p>
      </div>

      <div className="space-y-2">
        {hall.map((entry, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.05 }}
            className={`px-4 py-3 rounded-lg border ${
              entry.holder ? 'bg-[#1a1a00]/50 border-[#C9A84C]/20' : 'bg-[#111820] border-[#1A2535]'
            }`}
          >
            <div className="flex items-start gap-3">
              <span className={entry.holder ? 'text-[#FFD700]' : 'text-[#E8E0D0]/10'}>
                {CATEGORY_ICONS[entry.id] || <Trophy size={16} />}
              </span>
              <div className="flex-1">
                <h4 className="font-['Outfit'] text-xs font-bold text-[#E8E0D0]">{entry.name}</h4>
                <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/30">{entry.desc}</p>
                {entry.holder ? (
                  <div className="mt-1">
                    <span className="font-['Cormorant_Garamond'] text-sm font-bold text-[#FFD700]">{entry.holder}</span>
                    {entry.value && <span className="font-['Outfit'] text-[9px] text-[#C9A84C] ml-2">({entry.value})</span>}
                  </div>
                ) : (
                  <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/10 mt-1 italic">Unclaimed — be the first.</p>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// =============================================
// PREMIUM WATERFRONT STATUS — ENDGAME
// =============================================
const WaterfrontStatus = ({ gameId, onBack }) => {
  const [status, setStatus] = useState(null);
  const [collecting, setCollecting] = useState(false);
  const [buying, setBuying] = useState(null);
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/waterfront/status/${gameId}`);
      if (res.ok) setStatus(await res.json());
    } catch (e) {}
    setLoading(false);
  }, [gameId]);

  useEffect(() => { load(); }, [load]);

  const buyProperty = async (propertyId) => {
    setBuying(propertyId); setMsg(null);
    try {
      const res = await fetch(`${API}/api/waterfront/buy/${gameId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ property_id: propertyId }),
      });
      const d = await res.json();
      if (res.ok && d.success) {
        setMsg(d.narrator);
        load();
      } else {
        setMsg(d.detail || 'Failed');
      }
    } catch (e) { setMsg('Network error'); }
    setBuying(null);
  };

  const collectIncome = async () => {
    setCollecting(true); setMsg(null);
    try {
      const res = await fetch(`${API}/api/waterfront/collect/${gameId}`, { method: 'POST' });
      const d = await res.json();
      if (res.ok && d.success) {
        setMsg(d.narrator);
        load();
      } else {
        setMsg(d.detail || 'Failed');
      }
    } catch (e) { setMsg('Network error'); }
    setCollecting(false);
  };

  if (loading) return <p className="font-['Outfit'] text-[#E8E0D0]/20 text-xs text-center py-8">Loading...</p>;
  if (!status) return null;

  const { access, properties, daily_income, has_harbor_crown, can_collect, cooldown_remaining, owned_count, total_properties } = status;

  // Format cooldown
  const formatCooldown = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const TIER_COLORS = { 1: '#4A7AB5', 2: '#4ADE80', 3: '#C9A84C', 4: '#FFD700' };

  return (
    <div className="space-y-4" data-testid="waterfront-status">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest hover:text-[#C9A84C]" data-testid="waterfront-back">BACK</button>
      </div>

      <div className="text-center">
        <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#FFD700]" data-testid="wf-title">THE WATERFRONT</h3>
        <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest">PREMIUM DISTRICT — ENDGAME EMPIRE</p>
      </div>

      {/* Access gate */}
      {!access.unlocked ? (
        <div className="space-y-3" data-testid="waterfront-locked">
          <div className="bg-[#111820] border border-[#FFD700]/15 rounded-xl p-5 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#FFD700]/10 border border-[#FFD700]/20 flex items-center justify-center mb-3">
              <Crown size={24} className="text-[#FFD700]/40" />
            </div>
            <h4 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#E8E0D0]">WATERFRONT LOCKED</h4>
            <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] mt-1">Prove yourself to access the harbor.</p>
          </div>

          <div className="space-y-1.5">
            {[
              { label: `Complete Era 4`, met: access.era_complete, detail: `Current: Era ${access.current_era}` },
              { label: `Own 50+ Territories`, met: access.territories_met, detail: `${access.total_territories} owned` },
              { label: `Reach Prestige 1+`, met: access.prestige_met, detail: `Prestige ${access.prestige}` },
            ].map((req, i) => (
              <div key={i} className={`flex items-center justify-between px-3 py-2.5 rounded-lg border ${
                req.met ? 'bg-[#4ADE80]/5 border-[#4ADE80]/20' : 'bg-[#0a0c10] border-[#1A2535]'
              }`}>
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                    req.met ? 'bg-[#4ADE80]/20' : 'bg-[#1A2535]'
                  }`}>
                    {req.met ? (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                    ) : (
                      <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#E8E0D030" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>
                    )}
                  </div>
                  <span className={`font-['Outfit'] text-xs ${req.met ? 'text-[#4ADE80]' : 'text-[#E8E0D0]/40'}`}>{req.label}</span>
                </div>
                <span className="font-['Outfit'] text-[9px] text-[#E8E0D0]/20">{req.detail}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* Unlocked — property management */
        <div className="space-y-3" data-testid="waterfront-unlocked">
          {/* Income summary */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-[#111820] border border-[#FFD700]/15 rounded-lg p-2.5 text-center">
              <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/20 tracking-widest">PROPERTIES</p>
              <p className="font-['Outfit'] text-lg text-[#FFD700] font-bold">{owned_count}/{total_properties}</p>
            </div>
            <div className="bg-[#111820] border border-[#4ADE80]/15 rounded-lg p-2.5 text-center">
              <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/20 tracking-widest">DAILY INCOME</p>
              <p className="font-['Outfit'] text-lg text-[#4ADE80] font-bold">${daily_income.toLocaleString()}</p>
            </div>
            <div className="bg-[#111820] border border-[#C9A84C]/15 rounded-lg p-2.5 text-center">
              <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/20 tracking-widest">CROWN</p>
              <p className="font-['Outfit'] text-lg font-bold" style={{ color: has_harbor_crown ? '#FFD700' : '#E8E0D020' }}>
                {has_harbor_crown ? 'OWNED' : `${owned_count}/4`}
              </p>
            </div>
          </div>

          {has_harbor_crown && (
            <div className="bg-[#FFD700]/5 border border-[#FFD700]/20 rounded-lg p-3 text-center">
              <Crown size={20} className="text-[#FFD700] mx-auto mb-1" />
              <p className="font-['Cormorant_Garamond'] text-sm font-bold text-[#FFD700] italic">
                "You own the harbor. The water answers to you."
              </p>
              <p className="font-['Outfit'] text-[8px] text-[#C9A84C]/50 mt-1">+25% income bonus on all collections</p>
            </div>
          )}

          {/* Properties */}
          <div className="space-y-2">
            <p className="font-['Outfit'] text-[#FFD700] text-[9px] tracking-[0.3em]">PROPERTIES</p>
            {properties.map((prop) => {
              const tierColor = TIER_COLORS[prop.tier] || '#E8E0D0';
              return (
                <div key={prop.id}
                  className={`rounded-xl border overflow-hidden ${
                    prop.owned ? 'bg-[#111820] border-[#C9A84C]/20' : 'bg-[#0a0c10] border-[#1A2535]'
                  }`}
                  data-testid={`property-${prop.id}`}>
                  <div className="p-3">
                    <div className="flex items-start justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ background: tierColor + '15', border: `1px solid ${tierColor}30` }}>
                          <span className="font-['Outfit'] text-[10px] font-bold" style={{ color: tierColor }}>T{prop.tier}</span>
                        </div>
                        <div>
                          <h5 className="font-['Outfit'] text-sm font-bold text-[#E8E0D0]">{prop.name}</h5>
                          <p className="font-['Outfit'] text-[9px] text-[#E8E0D0]/30">{prop.desc}</p>
                        </div>
                      </div>
                      {prop.owned && (
                        <span className="font-['Outfit'] text-[7px] tracking-widest text-[#4ADE80] bg-[#4ADE80]/10 px-2 py-0.5 rounded-full border border-[#4ADE80]/20">OWNED</span>
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-3">
                        <span className="font-['Outfit'] text-[9px] text-[#4ADE80]">
                          <DollarSign size={10} className="inline -mt-0.5" />{prop.daily_income.toLocaleString()}/day
                        </span>
                        <span className="font-['Outfit'] text-[8px] text-[#E8E0D0]/15 italic">{prop.lore}</span>
                      </div>
                      {!prop.owned && (
                        <button onClick={() => buyProperty(prop.id)} disabled={buying === prop.id}
                          className="px-3 py-1.5 rounded-lg font-['Outfit'] text-[9px] tracking-wider transition-all"
                          style={{ background: tierColor + '15', border: `1px solid ${tierColor}30`, color: tierColor }}
                          data-testid={`buy-${prop.id}`}>
                          {buying === prop.id ? '...' : `$${prop.cost.toLocaleString()}`}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Collect button */}
          {owned_count > 0 && (
            <button onClick={collectIncome} disabled={collecting || !can_collect}
              className={`w-full py-3.5 rounded-xl font-['Outfit'] text-xs tracking-widest transition-all ${
                can_collect
                  ? 'bg-[#4ADE80]/10 border border-[#4ADE80]/30 text-[#4ADE80] hover:bg-[#4ADE80]/20'
                  : 'bg-[#1A2535]/50 border border-[#1A2535] text-[#E8E0D0]/20'
              }`}
              data-testid="collect-wf-income">
              {collecting ? 'COLLECTING...' : can_collect
                ? `COLLECT DAILY INCOME ($${daily_income.toLocaleString()}${has_harbor_crown ? ' +25%' : ''})`
                : `NEXT COLLECTION IN ${formatCooldown(cooldown_remaining)}`}
            </button>
          )}

          {msg && <p className="font-['Outfit'] text-[#C9A84C] text-xs text-center italic">"{msg}"</p>}
        </div>
      )}
    </div>
  );
};

export { HallOfFame, WaterfrontStatus };
